import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { RUOLI, IMG } from "@/lib/constants";
import { AppShell } from "@/components/layout/AppShell";
import { RoleBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { CaricoTeam } from "@/components/CaricoTeam";
import { OkrTeam, KudosButton, KudosFeed } from "@/components/TeamExtras";
import { InvitiTeam } from "@/components/InvitiTeam";
import { Heart } from "lucide-react";

const DESCR = {
  admin: "Gestione utenti e ruoli, supervisione complessiva.",
  project_manager: "Coordinamento, milestone, anagrafica operatore, piano servizi, invio telematico.",
  legale: "Requisiti, DGUE, dichiarazioni, antimafia, contrattualistica, clausole sociali.",
  commercialista: "Bilanci, PEF asseverato, fiscalità, cauzioni, offerta economica, indennizzo.",
  ingegnere: "Rilievi, catalogo interventi, efficientamento energetico, accessibilità, titoli edilizi.",
  marketing: "Identità, destagionalizzazione, eventi, famiglie, promozione digitale (criterio F).",
  commerciale: "Cliente, business plan, partner e fornitori della rete, accordi con l'uscente.",
};

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [kudos, setKudos] = useState({ items: [], conteggi: {} });
  const loadKudos = () => api.get("/kudos").then((r) => setKudos(r.data)).catch(() => {});
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); loadKudos(); }, []);
  const setRole = async (id, role) => {
    try { const { data } = await api.patch(`/users/${id}/role`, { role }); setUsers(users.map((u) => (u.id === id ? data : u))); toast.success("Ruolo aggiornato"); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <AppShell title="Team" subtitle="Le professionalità coinvolte nella preparazione delle domande">
      {["admin", "project_manager"].includes(user?.role) && <InvitiTeam />}
      <OkrTeam />
      <CaricoTeam />
      <section className="bg-white border border-slate-200 rounded-xl p-6 mb-8" data-testid="kudos-section"><div className="flex items-center gap-2 mb-4"><Heart size={18} className="text-pink-600" /><h3 className="font-heading text-lg font-medium text-slate-900">Kudos tra colleghi</h3><span className="ml-auto text-xs text-slate-500" data-testid="kudos-totale">{kudos.items.length} riconoscimenti</span></div><KudosFeed items={kudos.items} /></section>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="team-list">
        {Object.keys(RUOLI).map((role) => {
          const members = users.filter((u) => u.role === role);
          return (
            <div key={role} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`team-group-${role}`}>
              <div className="h-28 relative"><img src={IMG.roles[role]} alt={RUOLI[role].label} className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" /></div>
              <div className="p-6 -mt-4 relative">
              <div className="flex items-center justify-between"><RoleBadge role={role} /><span className="text-xs text-slate-500">{members.length} {members.length === 1 ? "membro" : "membri"}</span></div>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">{DESCR[role]}</p>
              <div className="mt-4 space-y-3">
                {members.map((u) => (
                  <div key={u.id} className="flex items-center gap-3" data-testid={`team-member-${u.id}`}>
                    <div className="h-9 w-9 rounded-full bg-slate-800 text-white flex items-center justify-center text-sm font-semibold overflow-hidden shrink-0">{u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" /> : u.name.slice(0, 1).toUpperCase()}</div>
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">{u.name}{kudos.conteggi[u.id] > 0 && <span className="inline-flex items-center gap-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-200 px-1.5 text-[10px]" data-testid={`kudos-count-${u.id}`}><Heart size={9} /> {kudos.conteggi[u.id]}</span>}</div><div className="text-xs text-slate-500 truncate">{u.email}</div></div>
                    <KudosButton dest={u} onDone={loadKudos} />
                    {user?.role === "admin" && (
                      <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-xs" data-testid={`team-role-select-${u.id}`}>
                        {Object.entries(RUOLI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                    )}
                  </div>
                ))}
                {members.length === 0 && <p className="text-xs text-slate-400">Nessun membro con questo ruolo</p>}
              </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
